import { CSSProperties, FC, useRef, useState } from 'react';
import { Result, Button, Skeleton, Divider, Grid, theme } from 'antd';
import { useSnapshot } from 'valtio';

import FlexBox from '../components/atoms/FlexBox';
import { FORM_PAGE_CSS_VARS } from '../components/atoms/FORM_PAGE_CSS_VARS';
import FormUI from '../components/rendering/FormUI';
import FormControl from '../components/artifacts/FormControl';
import FormSidebar from '../components/artifacts/FormSidebar';
import FormContextMenu from '../components/artifacts/form/FormContextMenu';
import FormCommands from '../components/artifacts/form/FormCommands';
import FormNoVersions from '../components/artifacts/form/FormNoVersions';
import FormRecoveryBanner from '../components/artifacts/form/FormRecoveryBanner';
import { CompositeFieldSidebar } from '../components/CompositeField';
import FormRequiredFieldsModal from '../components/modals/FormRequiredFieldsModal';
import { FormWorkflowActionProvider } from '../providers/artifacts/FormWorkflowActionProvider';
import { SidebarControlProvider, SidebarView } from '../providers/SidebarControlProvider';
import { useFormUndoKeybindings } from '../use/form/useFormUndoKeybindings';
import { useFormRecoveryRestore } from '../use/form/useFormRecoveryRestore';
import { useFormDeriveRefreshTimer } from '../use/useFormDeriveRefreshTimer';
import { useElementBoxSize } from '../use/useElementBoxSize';

import { formRecoveryStore, formStore } from '../store';

type FormPageContentStyle = CSSProperties &
	Partial<Record<'--page-chrome-height' | '--form-content-max-width', string>>;

/**
 * FormPageContent
 *
 * Main content component for the form page, with sidebar control. Renders one of
 * three states off the form store: a skeleton while the form/version/type load,
 * a not-found result on a form error, or the page chrome.
 *
 * A form is allowed to have zero versions (every version archived). That case is
 * detected on the `formVersions` collection being empty — not on a missing
 * `versionId`, which is the redirect-to-current-version path — and swaps
 * `FormUI`, which would render fully inert, for `FormNoVersions`. The
 * `FormControl` chrome stays mounted so the form number and navigation remain.
 *
 * `useFormUndoKeybindings` is called before those early returns — it owns the
 * route's undo/redo window listener, so it must run in every state. It is handed
 * `contentRef`, the element `FormUI` wraps the layout in: that subtree is exactly
 * the set of fields and cells registered with `formUndoStack`, and scopes which
 * focused surface the chord may be taken from. The second argument is the version
 * document this page edits, which is what bounds the stack: switching version in
 * place is a different document, and the entries recorded on the previous one do
 * not survive it.
 *
 * `useFormDeriveRefreshTimer` runs here for the same reason: the while-open refresh
 * trigger for Form Derived Values is defined by this page being mounted, so it is
 * called before the early returns and stops the moment the route unmounts.
 *
 * A point-in-time preview (SYD-2450) is one boolean off `formRecoveryStore`: it
 * hands `FormUI` its `forceDisabled`, which is a render-time read-only and never
 * contributes to `readOnlyReason` — only a genuine permission denial may set
 * that. `FormRecoveryBanner` sits inside the sticky div beside `FormControl`,
 * because outside it the notice would scroll away from the form it describes.
 *
 * `useFormRecoveryRestore` is mounted here, alongside `useFormUndoKeybindings`,
 * purely for its `registerTarget` effect: that is what keeps a restore's undo
 * entry reachable by ⌘Z after the confirmation modal that triggered it — and
 * the sidebar drawer around it — both close. `registerTarget` is refcounted,
 * so the confirmation modal's own instance of this hook registering the same
 * target a second time while it is open is harmless.
 *
 * The sticky header follows the Navb that `FormPage` measures: its `top` and
 * `width` read `--navb-height`/`--navb-width`, never `100vw` or a fixed offset,
 * so it stays under the Navb when that wraps on a phone. The header measures
 * itself too, and the outer column publishes `--page-chrome-height` (Navb plus
 * header) for ArTable's sticky offsets and `--form-content-max-width` (the Navb
 * width less the page padding) for the form layout's width cap. The measuring
 * hook runs above the early returns; its ref attaches once the header mounts.
 */
export const FormPageContent: FC = () => {
	const contentRef = useRef<HTMLDivElement>(null);
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const [activeView, setActiveView] = useState<SidebarView>('entity');
	const { token } = theme.useToken();
	const { useBreakpoint } = Grid;
	const { sm } = useBreakpoint();
	const pagePadding = sm ? token.padding : token.paddingSM;
	const [headerRef, headerSize] = useElementBoxSize<HTMLDivElement>();

	const snap = useSnapshot(formStore.state);
	const recoverySnap = useSnapshot(formRecoveryStore.state);
	const formError = snap.form.error;
	const formDocId = snap.formId;
	const versionDocId = snap.versionId;
	/**
	 * Read unconditionally, above the early returns: a `useSnapshot` that reads no
	 * property subscribes to every mutation of its store, and one point-in-time
	 * read writes five properties plus a trail entry.
	 */
	const previewing = recoverySnap.previewing;

	useFormUndoKeybindings(
		contentRef,
		formDocId && versionDocId ? `forms/${formDocId}/formVersions/${versionDocId}` : undefined,
	);
	useFormRecoveryRestore();

	useFormDeriveRefreshTimer();

	const requirementsMet =
		!snap.form.loading && !snap.formVersion.loading && !snap.formType.loading;
	const hasNoVersions = !snap.formVersions.loading && snap.formVersions.docIds.length === 0;

	if (!requirementsMet && !formError) {
		return <Skeleton active />;
	}

	if (formError) {
		return (
			<Result
				status='warning'
				title='Form Not Found'
				subTitle="The requested form does not exist or you don't have permission to view it."
				extra={<Button type='primary' onClick={() => window.history.back()}>Go Back</Button>}
			/>
		);
	}

	const columnStyle: FormPageContentStyle = {
		...(headerSize && {
			[FORM_PAGE_CSS_VARS.pageChromeHeight]: `calc(var(${FORM_PAGE_CSS_VARS.navbHeight}, 0px) + ${headerSize.height}px)`,
		}),
		[FORM_PAGE_CSS_VARS.formContentMaxWidth]: `calc(var(${FORM_PAGE_CSS_VARS.navbWidth}, 100%) - ${pagePadding * 2}px)`,
	};

	return (
		<SidebarControlProvider
			setIsSidebarOpen={setIsSidebarOpen}
			setActiveView={setActiveView}
		>
			<FormWorkflowActionProvider formId={formDocId || ''}>
			<FormCommands />
			<FormRequiredFieldsModal />
			<FormContextMenu formId={formDocId || ''}>
				<FlexBox column alignStart justifyStart noGrow stretch style={columnStyle}>
					<div
						ref={headerRef}
						style={{
							width: `var(${FORM_PAGE_CSS_VARS.navbWidth}, 100%)`,
							boxSizing: 'border-box',
							position: 'sticky',
							top: `var(${FORM_PAGE_CSS_VARS.navbHeight}, 0px)`,
							left: 0,
							marginInline: -pagePadding,
							paddingInline: pagePadding,
							zIndex: token.zIndexPopupBase - 1,
							backgroundColor: token.colorBgLayout,
						}}
					>
						<FormControl
							isSidebarOpen={isSidebarOpen}
							setIsSidebarOpen={setIsSidebarOpen}
						/>
						<FormRecoveryBanner />
						<Divider style={{ margin: 0 }} />
					</div>
					<FlexBox stretch>
						{hasNoVersions ? <FormNoVersions /> : <FormUI contentRef={contentRef} forceDisabled={previewing} />}
						<FormSidebar
							isOpen={isSidebarOpen}
							onClose={() => setIsSidebarOpen(false)}
							activeView={activeView}
							setActiveView={setActiveView}
						/>
						<CompositeFieldSidebar />
					</FlexBox>
				</FlexBox>
			</FormContextMenu>
			</FormWorkflowActionProvider>
		</SidebarControlProvider>
	);
};